import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../../services/api';
import { 
  EyeIcon, 
  PencilIcon, 
  ArrowUpIcon,
  ArrowDownIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import BookingDetail from './BookingDetail';
import BookingStatusUpdate from './BookingStatusUpdate';

const BookingsPage = () => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isStatusUpdateOpen, setIsStatusUpdateOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch bookings data
  const { data: bookingsData, isLoading, isError, refetch } = useQuery(
    ['adminBookings', sortField, sortDirection, filters, currentPage, itemsPerPage],
    async () => {
      try {
        console.log('Fetching bookings data...');
        
        // Use the diagnostic endpoint that we know works correctly
        const response = await api.get('/api/diagnostic/bookings', {
          params: {
            sort_field: sortField,
            sort_direction: sortDirection,
            page: currentPage,
            per_page: itemsPerPage,
            ...filters
          }
        });
        console.log('Bookings API response:', response.data);
        console.log('Bookings data structure:', JSON.stringify(response.data, null, 2));
        return response.data;
      } catch (error) {
        console.error('Error fetching bookings:', error);
        toast.error(`Failed to load bookings: ${error.message}`);
        throw error;
      }
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        console.error('Error fetching bookings:', error);
        toast.error('Failed to load bookings. Please try again.');
      }
    }
  );

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    refetch();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: '',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
    setCurrentPage(1);
    refetch();
  };

  // View booking details
  const viewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setIsDetailOpen(true);
  };

  // Update booking status
  const updateBookingStatus = (booking) => {
    setSelectedBooking(booking);
    setIsStatusUpdateOpen(true);
  };

  // Handle status update
  const handleStatusUpdate = async (bookingId, newStatus, notes) => {
    try {
      // Use the api service which automatically adds auth headers
      await api.put(`/api/v1/admin/bookings/${bookingId}/status`, {
        status: newStatus,
        notes: notes
      });
      
      refetch();
      setIsStatusUpdateOpen(false);
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('Failed to update booking status. Please try again.');
    }
  };

  // Function to render sort indicator
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 ml-1" /> 
      : <ArrowDownIcon className="h-4 w-4 ml-1" />;
  };

  // Function to render sort button
  const renderSortButton = (field, label) => (
    <button
      onClick={() => handleSort(field)}
      className="group inline-flex items-center text-left"
    >
      {label}
      <span className="ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
        {renderSortIcon(field)}
      </span>
    </button>
  );

  // Render status badge
  const renderStatusBadge = (status, paymentMethod) => {
    // Handle undefined or null status
    if (status === undefined || status === null) {
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
          Unknown
        </span>
      );
    }
    
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    let displayText = status;
    
    // Convert status to lowercase for case-insensitive comparison
    const statusLower = status.toLowerCase();
    
    switch (statusLower) {
      case 'confirmed':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'pending':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'cancelled':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'completed':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      case 'awaiting_office_payment':
        bgColor = 'bg-purple-100';
        textColor = 'text-purple-800';
        displayText = 'Awaiting Payment';
        break;
      case 'paid':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        displayText = 'Paid';
        break;
      default:
        break;
    }
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {displayText}
      </span>
    );
  };

  // Process and normalize the booking data from API
  const processBookings = (data) => {
    // Check if we have any data
    if (!data) return [];
    
    // Handle different response structures
    let bookingsArray = [];
    
    console.log('Processing API response structure:', data);
    
    // Check if we have a Laravel paginated response (this is the standard format from the regular API)
    if (data.data && data.data.data && Array.isArray(data.data.data)) {
      console.log('Processing Laravel paginated response');
      bookingsArray = data.data.data;
      
      // Store pagination metadata for UI if needed
      const pagination = {
        currentPage: data.data.current_page,
        lastPage: data.data.last_page,
        perPage: data.data.per_page,
        total: data.data.total
      };
      console.log('Pagination info:', pagination);
    }
    // Handle the diagnostic endpoint response format
    else if (data.success && data.data && Array.isArray(data.data)) {
      console.log('Processing diagnostic endpoint response');
      bookingsArray = data.data;
    } else if (Array.isArray(data)) {
      console.log('Processing array response');
      bookingsArray = data;
    } else if (data.data && Array.isArray(data.data)) {
      console.log('Processing data.data array response');
      bookingsArray = data.data;
    } else if (data.data) {
      console.log('Processing single data object response');
      bookingsArray = [data.data];
    }
    
    console.log('Extracted bookings array:', bookingsArray);
    return bookingsArray;
    
    console.log('Bookings array to process:', bookingsArray);
    
    // If bookings array is empty, return empty array
    if (bookingsArray.length === 0) {
      return [];
    }
    
    // Map and normalize each booking object
    return bookingsArray.map(booking => ({
      id: booking.id || Math.random().toString(36).substr(2, 9),
      booking_number: booking.booking_number || booking.id || 'N/A',
      customer: {
        name: booking.user?.name || booking.customer_name || 
              (typeof booking.customer_details === 'string' ? 
                JSON.parse(booking.customer_details)?.name : 
                booking.customer_details?.name) || 'N/A',
        email: booking.user?.email || booking.customer_email || 
               (typeof booking.customer_details === 'string' ? 
                 JSON.parse(booking.customer_details)?.email : 
                 booking.customer_details?.email) || 'N/A'
      },
      tour: {
        name: booking.tour?.name || 'Tour Information Unavailable',
        location: booking.tour?.location?.name || booking.tour?.location || 'Location Unavailable'
      },
      date: booking.travel_date || booking.booking_date,
      travelers: (booking.adults && booking.children) ? (parseInt(booking.adults) + parseInt(booking.children)) : 
                 booking.adults ? parseInt(booking.adults) : 
                 booking.num_travelers ? parseInt(booking.num_travelers) : 
                 booking.travelers ? parseInt(booking.travelers) : 0,
      total_amount: booking.total_amount || booking.total_price || 0,
      status: booking.status || booking.payment_status || 'pending',
      payment_status: booking.payment_status || 'pending',
      payment_method: booking.payment_method || 'unknown',
      created_at: booking.created_at || new Date().toISOString(),
      // Keep the original booking object for reference
      original: booking
    }));
  };
  
  // No mock data - we'll use real data from the API
  
  // Log the API response and processed data for debugging
  console.log('API response data:', bookingsData);
  
  // Process the API response data using our processBookings function
  const bookingsArray = bookingsData ? processBookings(bookingsData) : [];
  console.log('Processed bookings array:', bookingsArray);
  
  // Map each booking to a normalized format
  const processedBookings = bookingsArray.map(booking => {
    console.log('Processing booking:', booking);
    
    // Parse customer details if it's a JSON string
    let customerDetails = {};
    if (booking.customer_details) {
      try {
        if (typeof booking.customer_details === 'string') {
          customerDetails = JSON.parse(booking.customer_details);
        } else if (typeof booking.customer_details === 'object') {
          customerDetails = booking.customer_details;
        }
      } catch (error) {
        console.error('Error parsing customer details:', error);
      }
    }
    
    return {
      id: booking.id,
      booking_number: booking.booking_number || `BK-${booking.id}`,
      customer: {
        name: booking.user?.name || customerDetails.name || 'N/A',
        email: booking.user?.email || customerDetails.email || 'N/A'
      },
      tour: {
        name: booking.tour?.name || 'Tour Information Unavailable',
        location: booking.tour?.location || 'Location Unavailable'
      },
      date: booking.travel_date || booking.booking_date,
      travelers: (booking.adults && booking.children) ? 
                 (parseInt(booking.adults) + parseInt(booking.children)) : 
                 booking.adults ? parseInt(booking.adults) : 0,
      total_amount: booking.total_amount || 0,
      status: booking.status || 'pending',
      payment_status: booking.payment_status || 'pending',
      payment_method: booking.payment_method || 'unknown',
      created_at: booking.created_at,
      original: booking
    };
  });
  
  console.log('Processed bookings:', processedBookings);
  
  // Use only real data from the API
  const bookings = processedBookings;
  console.log('Final bookings to display (real data):', bookings);
  
  // Get pagination meta data from the API response
  const meta = bookingsData?.data?.meta || { current_page: 1, last_page: 1, total: processedBookings.length };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Bookings Management</h1>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <FunnelIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white shadow rounded-lg p-4">
          <form onSubmit={applyFilters} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700">
                  Date From
                </label>
                <input
                  type="date"
                  id="dateFrom"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700">
                  Date To
                </label>
                <input
                  type="date"
                  id="dateTo"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Booking #, Customer, Tour..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Reset
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Apply Filters
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Bookings Table */}
      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="animate-pulse p-6">
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        ) : isError ? (
          <div className="p-6 text-center">
            <div className="text-red-500 mb-4">Error loading bookings. Please try again.</div>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Retry
            </button>
          </div>
        ) : bookings.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No bookings found matching your criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('booking_number', 'Booking')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('customer.name', 'Customer')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('tour.name', 'Tour')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('date', 'Date')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('travelers', 'Travelers')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('total_amount', 'Amount')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('status', 'Status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('created_at', 'Created')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-primary">
                        {booking.booking_number || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {booking.customer?.name || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {booking.customer?.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{booking.tour?.name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">
                        {booking.tour?.location || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {booking.date ? new Date(booking.date).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {booking.travelers || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ${booking.total_amount ? parseFloat(booking.total_amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        // Check if it's a Pay Later booking
                        const isPendingPayLater = 
                          (booking.payment_status === 'pending' || booking.payment_status === 'awaiting_office_payment') && 
                          booking.payment_method === 'pay_later';
                        
                        if (isPendingPayLater) {
                          return (
                            <div>
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                Pay Later
                              </span>
                              <p className="text-xs text-gray-500 mt-1">Pending Payment</p>
                            </div>
                          );
                        }
                        
                        return renderStatusBadge(booking.status, booking.payment_method);
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {booking.created_at ? new Date(booking.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => viewBookingDetails(booking)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => updateBookingStatus(booking)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Update Status"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(meta.last_page, currentPage + 1))}
                disabled={currentPage === meta.last_page}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, meta.total)}
                  </span>{' '}
                  of <span className="font-medium">{meta.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ArrowUpIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  {/* Page numbers would go here */}
                  <button
                    onClick={() => setCurrentPage(Math.min(meta.last_page, currentPage + 1))}
                    disabled={currentPage === meta.last_page}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">Next</span>
                    <ArrowDownIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      {isDetailOpen && selectedBooking && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  onClick={() => setIsDetailOpen(false)}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <BookingDetail booking={selectedBooking} />
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {isStatusUpdateOpen && selectedBooking && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  onClick={() => setIsStatusUpdateOpen(false)}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <BookingStatusUpdate 
                booking={selectedBooking} 
                onUpdate={handleStatusUpdate} 
                onCancel={() => setIsStatusUpdateOpen(false)} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsPage;
